import { ACT_QUESTIONS } from '../tests/ACTTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getACTSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  if (total >= 20) return 'Asma controlada';
  if (total >= 16) return 'Asma parcialmente controlada';
  return 'Asma não controlada';
};

export const getACTSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  if (total >= 20) return 'bg-emerald-100 text-emerald-700';
  if (total >= 16) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

export const getACTScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  if (total >= 20) return '#10b981';
  if (total >= 16) return '#f59e0b';
  return '#f43f5e';
};

export const getACTInterpretationText = (total?: number | null): string => {
  if (typeof total !== 'number') {
    return 'Não há dados suficientes para interpretar o ACT.';
  }

  if (total >= 20) {
    return 'O ACT sugere asma controlada nas últimas quatro semanas, com baixa interferência dos sintomas na rotina e no sono.';
  }

  if (total >= 16) {
    return 'O ACT sugere controle parcial da asma, com sintomas ainda presentes e necessidade de revisão clínica do manejo e do seguimento.';
  }

  return 'O ACT sugere asma não controlada, com impacto relevante dos sintomas no dia a dia, no sono ou no uso de medicação de alívio.';
};

interface ACTResultRendererProps {
  payload:
    | {
        answers?: unknown;
        total?: unknown;
        classification?: unknown;
      }
    | null
    | undefined;
}

const getSelectedLabel = (questionIndex: number, rawScore: unknown): string => {
  const score = typeof rawScore === 'number' ? rawScore : Number.parseFloat(String(rawScore ?? ''));
  if (!Number.isFinite(score)) return 'Não respondido';
  return ACT_QUESTIONS[questionIndex]?.options.find((option) => option.score === score)?.label ?? 'Não respondido';
};

export default function ACTResultRenderer({ payload }: ACTResultRendererProps) {
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const total = typeof payload?.total === 'number' ? Math.max(5, Math.min(25, payload.total)) : null;
  const classification = typeof payload?.classification === 'string' ? payload.classification : getACTSeverityLabel(total);
  const interpretation = resolveAssessmentInterpretationText(payload, getACTInterpretationText(total));

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação ACT"
        value={total ?? '—'}
        valueColor={getACTScoreColor(total)}
        valueCaption={typeof total === 'number' ? `${total}/25` : 'Pontuação indisponível'}
        classification={classification}
        supportingText="Valores mais altos indicam melhor controle da asma."
        interpretation={interpretation}
      >
        <div className="space-y-2">
          <div className="relative py-1">
            <div
              className="h-9 rounded-full"
              style={{ background: 'linear-gradient(90deg, #f43f5e 0%, #f59e0b 55%, #22c55e 100%)' }}
            />
            {typeof total === 'number' ? (
              <div
                className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800"
                style={{ left: `calc(${((total - 5) / 20) * 100}% - 2px)` }}
              />
            ) : null}
          </div>
          <div className="flex items-center justify-between text-xs font-medium text-slate-500">
            <span>5-15</span>
            <span>16-19</span>
            <span>20-25</span>
          </div>
        </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Questão</th>
              <th className="px-6 py-4">Resposta selecionada</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {ACT_QUESTIONS.map((item, index) => {
              const score = typeof answers[index] === 'number' ? answers[index] : null;
              return (
                <tr key={`act-item-${index + 1}`} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">{index + 1}. {item.prompt}</td>
                  <td className="px-6 py-4 text-slate-700">{getSelectedLabel(index, score)}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{score ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
